/*
 * RZ-DS — the design system's behaviour (storefront v2, 2026-10-30).
 * Deferred, no dependencies, ~4 KB. Everything here is an enhancement: with no
 * script the menu panel opens on hover/focus in CSS, every section is visible
 * and every story circle is a plain link to its collection.
 *
 *   1. reveal       one-time fade-up of below-the-fold blocks (IntersectionObserver)
 *   2. mega menu    click / Enter / hover-intent, Escape and outside click close
 *   3. stories      mobile-only story circles: the <template> is cloned on a
 *                   phone only, and a tap opens a story viewer of that
 *                   collection's products, fetched on the tap
 */
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. reveal ─────────────────────────────────────────────────────── */
  function reveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll('.rzx-reveal:not(.is-in)'));
    if (!els.length) return;
    function show(e) { e.classList.add('is-in'); }
    if (reduce || !('IntersectionObserver' in window)) { els.forEach(show); return; }
    /* Additive only: anything already on screen is shown at once (never faded
       in under the reader), and a failsafe shows everything a few seconds after
       load, so a missed observer callback can never leave content invisible. */
    var vh = window.innerHeight || 800;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { show(en.target); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
    els.forEach(function (e) { if (e.getBoundingClientRect().top < vh) show(e); else io.observe(e); });
    function all() { els.forEach(show); io.disconnect(); }
    if (document.readyState === 'complete') setTimeout(all, 3000); else window.addEventListener('load', function () { setTimeout(all, 3000); });
  }

  /* ── 2. mega menu ──────────────────────────────────────────────────── */
  function mega() {
    var item = document.querySelector('[data-rzx-mega]');
    if (!item) return;
    item.setAttribute('data-rzx-bound', '');
    var btn = item.querySelector('[data-rzx-mega-trigger]');
    var panel = item.querySelector('[data-rzx-mega-panel]');
    var timer = 0;
    var promo = panel.querySelector('template[data-rzx-promo]');
    function set(open) {
      clearTimeout(timer);
      if (open && promo) { promo.parentNode.replaceChild(promo.content.cloneNode(true), promo); promo = null; }
      if (open) item.setAttribute('data-open', ''); else item.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    btn.addEventListener('click', function () { set(btn.getAttribute('aria-expanded') !== 'true'); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') { set(false); btn.focus(); }
    });
    document.addEventListener('click', function (e) { if (!item.contains(e.target)) set(false); });
    item.addEventListener('focusout', function (e) { if (!item.contains(e.relatedTarget)) set(false); });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      item.addEventListener('pointerenter', function () { clearTimeout(timer); timer = setTimeout(function () { set(true); }, 120); });
      item.addEventListener('pointerleave', function () { clearTimeout(timer); timer = setTimeout(function () { set(false); }, 220); });
    }
    panel.addEventListener('click', function (e) { if (e.target.closest('a')) set(false); });
  }

  /* ── 3. stories ────────────────────────────────────────────────────── */
  function stories() {
    var roots = document.querySelectorAll('[data-rzx-stories-root]');
    if (!roots.length || !window.matchMedia('(max-width: 989px)').matches) return;
    roots.forEach(function (root) {
      var tpl = root.querySelector('template[data-rzx-stories]');
      var mount = root.querySelector('[data-rzx-stories-mount]');
      if (!tpl || !mount || mount.childElementCount) return;
      mount.appendChild(tpl.content.cloneNode(true));
      mount.addEventListener('click', function (e) {
        var a = e.target.closest('a[data-rzx-story]');
        if (!a || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        openViewer(a.getAttribute('data-rzx-story'), a.getAttribute('href'), a.getAttribute('data-title') || '');
      });
    });
  }

  var viewer = null;
  /* WCAG 2.2.1/2.2.2: it advances on its own every 5 s but has a visible pause
     (Space too), stops on the last rug instead of closing, and the stage is only
     announced when a person moves it, never while it plays. */
  function openViewer(handle, href, title) {
    if (viewer) viewer.remove();
    var d = document.createElement('dialog');
    d.className = 'rzx-story';
    d.setAttribute('aria-label', title);
    d.innerHTML = '<div class="rzx-story__bars" aria-hidden="true"></div>' +
      '<div class="rzx-story__top"><strong class="rzx-story__title"></strong>' +
      '<span><button type="button" class="rzx-story__btn" data-pause aria-pressed="false">Pause</button>' +
      '<button type="button" class="rzx-story__close" aria-label="Close">✕</button></span></div>' +
      '<div class="rzx-story__stage" aria-live="off"><p class="rzx-story__msg">Loading…</p></div>' +
      '<button type="button" class="rzx-story__nav rzx-story__nav--prev" aria-label="Previous rug"></button>' +
      '<button type="button" class="rzx-story__nav rzx-story__nav--next" aria-label="Next rug"></button>' +
      /* round 11 (the owner): under the rug, left and right arrows, "View this rug" and "See all" */
      '<div class="rzx-story__foot">' +
      '<button type="button" class="rzx-story__arrow" data-step="-1" aria-label="Previous rug"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M14.5 5.5 8 12l6.5 6.5"/></svg></button>' +
      '<a class="rzx-btn rzx-btn--primary rzx-story__go">View this rug</a>' +
      '<a class="rzx-btn rzx-btn--light rzx-story__all">See all</a>' +
      '<button type="button" class="rzx-story__arrow" data-step="1" aria-label="Next rug"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9.5 5.5 16 12l-6.5 6.5"/></svg></button>' +
      '</div>';
    d.querySelector('.rzx-story__title').textContent = title;
    var all = d.querySelector('.rzx-story__all'); all.href = href; all.setAttribute('aria-label', 'See all: ' + title);
    var goA = d.querySelector('.rzx-story__go'), arrows = d.querySelectorAll('.rzx-story__arrow');
    document.body.appendChild(d);
    viewer = d;
    d.showModal();
    var items = [], i = 0, t = 0, DUR = 5000, paused = reduce;
    var stage = d.querySelector('.rzx-story__stage'), pbtn = d.querySelector('[data-pause]');
    function close() { clearTimeout(t); d.close(); d.remove(); viewer = null; }
    function setPaused(v) { paused = v; pbtn.setAttribute('aria-pressed', v ? 'true' : 'false'); pbtn.textContent = v ? 'Play' : 'Pause'; d.classList.toggle('is-held', v); stage.setAttribute('aria-live', v ? 'polite' : 'off'); if (v) clearTimeout(t); else schedule(); }
    function schedule() { clearTimeout(t); if (!paused && i < items.length - 1) t = setTimeout(function () { show(i + 1); }, DUR); }
    function show(n, byHand) {
      if (!items.length) return;
      i = Math.max(0, Math.min(items.length - 1, n));
      stage.setAttribute('aria-live', (byHand || paused) ? 'polite' : 'off');
      var hadFocus = stage.contains(document.activeElement);
      var p = items[i];
      stage.innerHTML = '';
      var a = document.createElement('a'); a.className = 'rzx-story__card'; a.href = '/products/' + encodeURIComponent(p.h);
      var img = document.createElement('img'); img.src = p.i; img.alt = ''; img.width = 828; img.height = 828;
      var nm = document.createElement('span'); nm.className = 'rzx-story__name'; nm.textContent = p.t;
      var pr = document.createElement('span'); pr.className = 'rzx-story__price'; pr.textContent = 'From ' + p.p;
      a.appendChild(img); a.appendChild(nm); a.appendChild(pr); stage.appendChild(a);
      goA.href = a.href;
      arrows[0].disabled = i === 0;
      arrows[1].disabled = i === items.length - 1;
      if (hadFocus) a.focus();
      d.querySelectorAll('.rzx-story__bar').forEach(function (b, k) { b.className = 'rzx-story__bar' + (k < i ? ' is-done' : k === i ? ' is-on' : ''); });
      schedule();
    }
    d.querySelector('.rzx-story__close').addEventListener('click', close);
    pbtn.addEventListener('click', function () { setPaused(!paused); });
    d.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    d.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1, true); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1, true); }
      else if (e.key === ' ' && e.target === d) { e.preventDefault(); setPaused(!paused); }
    });
    d.querySelector('.rzx-story__nav--prev').addEventListener('click', function () { show(i - 1, true); });
    d.querySelector('.rzx-story__nav--next').addEventListener('click', function () { show(i + 1, true); });
    arrows.forEach(function (b) { b.addEventListener('click', function () { show(i + (parseInt(b.getAttribute('data-step'), 10) || 1), true); }); });
    stage.addEventListener('pointerdown', function () { if (!paused) { clearTimeout(t); d.classList.add('is-held'); } });
    stage.addEventListener('pointerup', function () { if (!paused) { d.classList.remove('is-held'); schedule(); } });
    /* A keyboard user on the rug's link is reading it: the timer waits until focus leaves. */
    stage.addEventListener('focusin', function () { clearTimeout(t); });
    stage.addEventListener('focusout', function () { if (!paused) schedule(); });
    fetch('/collections/' + encodeURIComponent(handle) + '?view=rzx-story')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        items = Array.isArray(j) ? j : [];
        if (!items.length) { window.location.href = href; return; }
        d.querySelector('.rzx-story__bars').innerHTML = items.map(function () { return '<span class="rzx-story__bar"><i></i></span>'; }).join('');
        d.style.setProperty('--rzx-story-dur', DUR + 'ms');
        if (reduce) setPaused(true);
        show(0);
      })
      .catch(function () { window.location.href = href; });
  }

  /* ── 4. tabs ───────────────────────────────────────────────────────── */
  /* [data-rzx-tabs] holds a [data-rzx-tablist] of buttons naming panel ids.
     With no script every panel shows, stacked, under its own fallback title. */
  function tabs() {
    document.querySelectorAll('[data-rzx-tabs]:not(.rzx-tabs--on)').forEach(function (root) {
      var list = root.querySelector('[data-rzx-tablist]');
      var btns = Array.prototype.slice.call(list.querySelectorAll('[data-rzx-tab]'));
      list.setAttribute('role', 'tablist');
      function select(k, focus) {
        btns.forEach(function (b, j) {
          var on = j === k, panel = document.getElementById(b.getAttribute('data-rzx-tab'));
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
          if (panel) panel.hidden = !on;
          if (on && focus) b.focus();
        });
      }
      btns.forEach(function (b, j) {
        var id = b.getAttribute('data-rzx-tab'), panel = document.getElementById(id);
        b.setAttribute('role', 'tab'); b.id = b.id || id + '-tab'; b.setAttribute('aria-controls', id);
        if (panel) { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', b.id); }
        b.addEventListener('click', function () { select(j); });
        b.addEventListener('keydown', function (e) {
          var k = e.key === 'ArrowRight' ? j + 1 : e.key === 'ArrowLeft' ? j - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? btns.length - 1 : null;
          if (k === null) return;
          e.preventDefault(); select((k + btns.length) % btns.length, true);
        });
      });
      root.classList.add('rzx-tabs--on');
      select(0);
    });
  }


  /* ── 5. recently viewed ────────────────────────────────────────────── */
  function recent() {
    var KEY = 'rzx-recent', list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return; }
    var selfEl = document.querySelector('[data-rzx-recent-self]'), self = null;
    if (selfEl) {
      try { self = JSON.parse(selfEl.textContent); } catch (e) { self = null; }
      if (self && self.h) {
        list = [self].concat(list.filter(function (x) { return x.h !== self.h; })).slice(0, 12);
        try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
      }
    }
    var box = document.querySelector('[data-rzx-recent]');
    if (!box) return;
    var show = list.filter(function (x) { return !self || x.h !== self.h; }).slice(0, 8);
    if (!show.length) return;
    var ul = box.querySelector('[data-rzx-recent-list]');
    ul.innerHTML = '';
    show.forEach(function (x) {
      var li = document.createElement('li'), a = document.createElement('a');
      a.className = 'rzx-recent__card'; a.href = x.u;
      var img = document.createElement('img');
      img.src = x.i; img.alt = ''; img.width = 400; img.height = 400; img.loading = 'lazy';
      var name = document.createElement('span'); name.className = 'rzx-recent__name'; name.textContent = x.t;
      var price = document.createElement('span'); price.className = 'rzx-recent__price'; price.textContent = 'From ' + x.p;
      a.appendChild(img); a.appendChild(name); a.appendChild(price); li.appendChild(a); ul.appendChild(li);
    });
    box.hidden = false;
  }

  /* ── 6. Rug Finder progress ───────────────────────────────────────── */
  /* rug-finder.js sets data-screen on its root at every move; this mirrors it
     into a real progress bar and an announced "Step n of 4" line. */
  function finderSteps() {
    var root = document.querySelector('[data-rug-finder]');
    var box = document.querySelector('[data-rzx-finder-steps]');
    if (!root || !box || box.dataset.bound) return;
    box.dataset.bound = '1';
    var names = ['Start', 'Your room photo', 'What we saw', 'Your rugs'];
    var bar = box.querySelector('[role=progressbar]'), fill = box.querySelector('.rzx-fsteps__fill'), txt = box.querySelector('.rzx-fsteps__text');
    function sync() {
      var n = parseInt(root.getAttribute('data-screen') || '1', 10) || 1;
      box.hidden = n < 2;
      bar.setAttribute('aria-valuenow', String(n));
      bar.setAttribute('aria-valuetext', 'Step ' + n + ' of 4');
      fill.style.transform = 'scaleX(' + (n / 4) + ')';
      var t = 'Step ' + n + ' of 4: ' + names[n - 1];
      if (txt.textContent !== t) txt.textContent = t;
      /* round 5: the four named stops on the line (sections/rug-finder.liquid) */
      box.querySelectorAll('[data-stop]').forEach(function (li) {
        var k = parseInt(li.getAttribute('data-stop'), 10);
        li.classList.toggle('is-done', k < n);
        li.classList.toggle('is-on', k === n);
      });
    }
    new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['data-screen'] });
    sync();
  }


  /* ── 7. size grid ──────────────────────────────────────────────────── */
  /* Round 5 (the owner, 2026-10-30): EVERY size shows — no "More sizes".
     The nine-first code and its MutationObserver are gone. */

  /* ── 7b. the stars under the name take you to the reviews (round 5) ──
     Delegated, because a size change morphs the product section and a
     listener bound to the old stars would go with them. */
  function starsToReviews() {
    document.querySelectorAll('.rz-pdp-stars').forEach(function (el) {
      el.setAttribute('role', 'link'); el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', 'Read the customer reviews');
    });
  }
  function goReviews(e) {
    var el = e.target && e.target.closest && e.target.closest('.rz-pdp-stars');
    if (!el) return;
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    var t = document.getElementById('looxReviews') || document.querySelector('[id*="178874784978bc859b"], .loox-reviews-default');
    if (!t) return;
    e.preventDefault();
    t.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }
  document.addEventListener('click', goReviews);
  document.addEventListener('keydown', goReviews);

  /* ── 7c. the price names its size (round 5) ── */
  function priceSize() {
    var c = document.querySelector('.rz-sg__grid:not([hidden]) .rz-sg__card[aria-checked="true"] .rz-sg__nominal');
    var t = c ? c.textContent.trim() : '';
    document.querySelectorAll('[data-rz-price-size-row]').forEach(function (row) {
      var b = row.querySelector('[data-rz-price-size]');
      if (b && b.textContent !== t) b.textContent = t;
      if (row.hidden !== !t) row.hidden = !t;
    });
    /* the sticky bar names the same size the shopper tapped */
    if (t) document.querySelectorAll('.rz-sticky__size').forEach(function (n) { if (n.textContent !== t) n.textContent = t; });
  }
  /* ── 7e. the two lines under the title (round 8, blocks/rz-pdp-lead) ──
     Line 2 is the one TRUE scarcity: a woven size with few left (Shopify's tracked count),
     else the last order day the delivery box's own arithmetic (window.rzEta2) can still
     get to a holiday, else the arrival date. Every write compares first. */
  var leadVariant = null;
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  function holidaysOf(y) {
    var nov1 = new Date(Date.UTC(y, 10, 1)), firstThu = 1 + (4 - nov1.getUTCDay() + 7) % 7;
    return [{ name: 'Thanksgiving', d: new Date(Date.UTC(y, 10, firstThu + 21)) },
            { name: 'Christmas', d: new Date(Date.UTC(y, 11, 25)) }];
  }
  function leadHtml(box) {
    var E = window.rzEta2;
    if (!E || !E.arriveOf) return null;
    var D = E.today(), arrive = E.arriveOf(D);
    var id = leadVariant || box.getAttribute('data-variant'), left = 0;
    var s = box.querySelector('[data-rz-lead-stock]');
    if (s && id) { try { left = JSON.parse(s.textContent)[id] || 0; } catch (e) { left = 0; } }
    if (left > 0) return left + ' left in this size. Arrives <b>' + E.label(arrive) + '</b>.';
    var y = D.getUTCFullYear(), hs = holidaysOf(y).concat(holidaysOf(y + 1));
    for (var i = 0; i < hs.length; i++) {
      var last = E.addDays(hs[i].d, -1), n = 0;
      while (E.arriveOf(last).getTime() >= hs[i].d.getTime() && n++ < 10) last = E.addDays(last, -1);
      var days = (last.getTime() - D.getTime()) / 864e5;
      /* round 10 (the owner): 'Order today', never the last day's name — the line still shows only while today
         is on or before that last day, so it stays true. Round 11 (the owner, his final form): the holiday and
         ITS OWN date, never the arrival date — "Order today for Thanksgiving, November 26." */
      if (days >= 0 && days <= 45) return 'Order today for <b>' + hs[i].name + ', ' + MONTHS[hs[i].d.getUTCMonth()] + ' ' + hs[i].d.getUTCDate() + '</b>.';
    }
    return 'Order today, at your door by <b>' + E.label(arrive) + '</b>.';
  }
  function paintLead() {
    document.querySelectorAll('[data-rz-lead]').forEach(function (box) {
      var html = leadHtml(box), n = box.querySelector('[data-rz-lead-now]');
      if (html && n && n.innerHTML !== html) n.innerHTML = html;
    });
  }
  function leadVariantOf(el) {
    if (!el || !el.dataset) return null;
    if (el.tagName === 'SELECT') { var o = el.options[el.selectedIndex]; return o && o.dataset && o.dataset.optionName ? o.dataset.variantId || null : null; }
    return el.dataset.optionName ? el.dataset.variantId || null : null;
  }
  /* a SHAPE press reaches the variant only through RugInstantPrice.apply (rug-size-grid.js);
     that file loads after this one, so the wrap waits for DOMContentLoaded */
  function wrapInstantPrice() {
    var ip = window.RugInstantPrice;
    if (!ip || !ip.apply || ip.apply.rzLead) return;
    var orig = ip.apply;
    ip.apply = function (id) { var r = orig.apply(this, arguments); if (id) { leadVariant = String(id); paintLead(); } return r; };
    ip.apply.rzLead = true;
  }
  var leadWired = false;
  function watchLead() {
    if (!document.querySelector('[data-rz-lead]')) return;
    paintLead();
    if (leadWired) return;
    leadWired = true;
    window.addEventListener('change', function (e) {
      var id = leadVariantOf(e.target);
      if (id) { leadVariant = String(id); paintLead(); }
    }, true);
    wrapInstantPrice();
    document.addEventListener('DOMContentLoaded', wrapInstantPrice);
  }

  function watchPriceSize() {
    /* round 7: the line under the price is gone (the owner); the sticky bar still names the size.
       round 8: the same observer repaints the line under the title after Horizon's morph. */
    if (!document.querySelector('[data-rz-price-size-row], .rz-sticky__size, [data-rz-lead]')) return;
    var both = function () { priceSize(); paintLead(); };
    both();
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.rz-sg__card')) requestAnimationFrame(both);
    });
    var q = false;
    new MutationObserver(function () {
      if (q) return; q = true;
      requestAnimationFrame(function () { q = false; both(); });
    }).observe(document.querySelector('main') || document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-checked', 'hidden'] });
  }

  /* ── 7d. decorative loops run only on screen (round 6c, speed) ──
     Measured on a product page: ~60 style recalculations a second at idle from the
     decorative loops (gift light, guarantee sheen, button nudge, review marquee), 2 with
     them stopped. Each container's own animations are paused while it is off screen and
     played when it comes back — through getAnimations(), so no CSS selector cost. */
  var ANIM_GATES = '.rz-gifts, .rz-risk, .buy-buttons-block, .rzx-marquee, .rz6-hstories, .rz-guarantee, .rz-lead';
  function animGates() {
    if (!('IntersectionObserver' in window) || !document.getAnimations) return;
    var io = new IntersectionObserver(function (es) {
      var all = document.getAnimations();
      es.forEach(function (e) {
        for (var i = 0; i < all.length; i++) {
          var t = all[i].effect && all[i].effect.target;
          if (t && e.target.contains(t)) {
            if (e.isIntersecting) { if (all[i].playState === 'paused') all[i].play(); }
            else if (all[i].playState === 'running') all[i].pause();
          }
        }
      });
    });
    document.querySelectorAll(ANIM_GATES).forEach(function (el) { io.observe(el); });
  }

  /* ── 8. a question named in the URL opens itself ─────────────────────── */
  function openHashed() {
    var id = decodeURIComponent((window.location.hash || '').slice(1));
    if (!id) return;
    var d = document.getElementById(id);
    if (d && d.tagName === 'DETAILS') { d.open = true; d.scrollIntoView({ block: 'start' }); }
  }


  /* ── 9. gallery: nothing focusable inside a hidden slide ──────────────── */
  /* The wishlist mounts a Save heart on every gallery slide; on the slides
     Horizon marks aria-hidden those hearts were still Tab stops (axe:
     aria-hidden-focus). Their tabindex follows the slide's visibility. */
  function galleryFocus() {
    var root = document.querySelector('.product-information slideshow-slides, .product-media-gallery slideshow-slides');
    if (!root || root.dataset.rzxGf) return;
    root.dataset.rzxGf = '1';
    function sync() {
      root.querySelectorAll('slideshow-slide').forEach(function (sl) {
        var hidden = sl.getAttribute('aria-hidden') === 'true';
        sl.querySelectorAll('button, a[href]').forEach(function (el) {
          if (hidden) { if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1'); }
          else if (el.getAttribute('tabindex') === '-1') el.removeAttribute('tabindex');
        });
      });
    }
    sync();
    new MutationObserver(function (rs) {
      if (rs.some(function (r) { return r.attributeName === 'aria-hidden' || r.type === 'childList'; })) sync();
    }).observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'], childList: true });
  }

  /* the cart's "Free gifts" frame (the owner, 2026-10-31): the open state is on <html>, so the drawer re-rendering
     the cart does not close it; every toggle on the page says the same state */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-rz-giftbox-toggle]') : null;
    if (!b) return;
    var open = !document.documentElement.hasAttribute('data-rz-gifts-open');
    document.documentElement.toggleAttribute('data-rz-gifts-open', open);
    document.querySelectorAll('[data-rz-giftbox-toggle]').forEach(function (x) { x.setAttribute('aria-expanded', open ? 'true' : 'false'); });
  });

  function init() { reveal(); mega(); stories(); tabs(); recent(); finderSteps(); starsToReviews(); watchPriceSize(); watchLead(); animGates(); openHashed(); galleryFocus(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
